import React, { useEffect, useRef, useState } from 'react'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'

import './CommentComposer.scss'

// =============================================================================
// CommentComposer — blurple-bordered textarea box for composing a comment.
// Shared by the agent drawer (DrawerCommentsPanel), the selection-anchored
// popover (SelectionCommentPopover) and the in-card edit box on a comment the
// viewer still owns (ChangeRequestCard). ⌘↵ to save / Esc to cancel.
// =============================================================================

/**
 * @param {Function} props.onSubmit called with the trimmed message
 * @param {Function} props.onCancel dismiss without saving
 * @param {boolean} [props.busy] request in flight — blocks submit
 * @param {string} [props.initialValue] prefill, for editing an existing comment
 * @param {string} [props.submitLabel] defaults to Submit; editing says Save
 * @param {string} [props.busyLabel] in-flight label for the submit button
 * @param {string} [props.placeholder]
 * @param {string} [props.ariaLabel] accessible name for the textarea
 * @param {string} [props.dataTestId] test-id prefix, so an edit box inside a
 *   card doesn't collide with a compose box open in the same list
 */
const CommentComposer = ({
  onSubmit,
  onCancel,
  busy,
  initialValue = '',
  submitLabel = 'Submit',
  busyLabel = 'Submitting…',
  placeholder = 'Add a comment…',
  ariaLabel = 'Add a comment',
  dataTestId = 'comment-composer',
}) => {
  const [message, setMessage] = useState(initialValue)
  const textareaRef = useRef(null)

  // Focus on mount, but never let the focus scroll anything. `autoFocus` did,
  // and this box renders inside a position:fixed popover that is itself a
  // descendant of a scrollable panel — so the browser tried to "reveal" a fixed
  // element inside a scroll container it has no real offset in, and shunted the
  // panel's whole content sideways out of view. The drawer looked like it had
  // been wiped; it had only been scrolled 900-odd pixels left.
  useEffect(() => {
    textareaRef.current?.focus({ preventScroll: true })
  }, [])

  const trimmed = message.trim()
  // Re-submitting the text unchanged is a no-op write, so it reads as disabled
  // rather than as a save that did nothing.
  const canSubmit = !busy && !isEmpty(trimmed) && trimmed !== initialValue.trim()

  const submit = () => {
    if (canSubmit) {
      onSubmit(trimmed)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Stop the bubble so Esc cancels the composer without also closing the
      // surrounding drawer/popover.
      event.preventDefault()
      event.stopPropagation()
      onCancel()
    } else if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      // Prevent the keystroke from inserting a newline in the textarea.
      event.preventDefault()
      submit()
    }
  }

  return (
    <div className="comment-composer" data-testid={dataTestId}>
      <textarea
        className="comment-composer-textarea"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={handleKeyDown}
        rows={3}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid={`${dataTestId}-textarea`}
        ref={textareaRef}
      />
      <div className="comment-composer-footer">
        <span className="comment-composer-kbd-hint">⌘↵ to save · Esc to cancel</span>
        <div className="comment-composer-actions">
          <Button small onClick={onCancel} data-testid={`${dataTestId}-cancel`}>
            Cancel
          </Button>
          <Button
            brand
            small
            onClick={submit}
            disabled={!canSubmit}
            data-testid={`${dataTestId}-submit`}
          >
            {busy ? busyLabel : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CommentComposer
